import { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, FileDown, FileSpreadsheet, FileUp, History, Landmark, RotateCcw, Search, XCircle } from "lucide-react";

type LinhaImportada = {
  arquivoNome: string;
  abaNome: string;
  numeroLinha: number;
  dataMovimentacao: string | null;
  pagador: string | null;
  seuNumero: string | null;
  nossoNumero: string | null;
  vencimento: string | null;
  valorInicial: number | null;
  valorFinal: number | null;
  descricaoOperacao: string | null;
};

type LinhaBaixa = LinhaImportada & {
  id: number;
  status: string;
  mensagem: string;
  metodoCruzamento: string;
  contaReceberId: number | null;
  boletoId: number | null;
  candidatos: Array<{ contaReceberId: number; boletoId: number; parcelaDescricao: string; destinatarioNome: string; contaReceberStatus: string; boletoStatus: string }>;
  baixadoPorNome: string | null;
  baixadoEm: string | null;
};

type Baixa = {
  id: number;
  status: string;
  emitenteId: number | null;
  criadoPorNome: string | null;
  criadoEm: string;
  atualizadoPorNome: string | null;
  atualizadoEm: string;
  arquivos: string[];
  linhas: LinhaBaixa[];
  historico: Array<{ id: number; evento: string; descricao: string; userName: string | null; criadoEm: string }>;
};

function texto(valor: unknown) {
  return String(valor ?? "").trim();
}

function normalizarCabecalho(valor: unknown) {
  return texto(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
}

function dataExcel(valor: unknown): string | null {
  if (!valor) return null;
  if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
    return `${valor.getFullYear()}-${String(valor.getMonth() + 1).padStart(2, "0")}-${String(valor.getDate()).padStart(2, "0")}`;
  }
  if (typeof valor === "number") {
    const partes = XLSX.SSF.parse_date_code(valor);
    if (!partes) return null;
    return `${partes.y}-${String(partes.m).padStart(2, "0")}-${String(partes.d).padStart(2, "0")}`;
  }
  const encontrado = texto(valor).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (encontrado) return `${encontrado[3]}-${encontrado[2]}-${encontrado[1]}`;
  return null;
}

function numeroExcel(valor: unknown): number | null {
  if (typeof valor === "number" && Number.isFinite(valor)) return valor;
  const convertido = Number(texto(valor).replaceAll(".", "").replace(",", "."));
  return Number.isFinite(convertido) ? convertido : null;
}

function moeda(valor: number | null | undefined) {
  return Number(valor ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function dataBr(valor: string | Date | null | undefined) {
  if (!valor) return "-";
  if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? "-" : valor.toLocaleDateString("pt-BR");
  const [ano, mes, dia] = valor.slice(0, 10).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
}

function dataHoraBr(valor: string | null | undefined) {
  if (!valor) return "-";
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? valor : data.toLocaleString("pt-BR");
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    PENDENTE: "Pendente",
    FINALIZADO: "Finalizado",
    FINALIZADO_PARCIALMENTE: "Finalizado parcialmente",
    CANCELADO: "Cancelado",
    PRONTO_PARA_BAIXA: "Pronto para baixa",
    JA_PAGO: "Já pago",
    AMBIGUO: "Ambíguo",
    NAO_ENCONTRADO: "Não encontrado",
    NAO_ELEGIVEL: "Não elegível",
    BAIXADO: "Baixado",
    DIVERGENCIA: "Divergência",
  };
  return labels[status] ?? status;
}

function statusClass(status: string) {
  if (["PRONTO_PARA_BAIXA", "BAIXADO", "FINALIZADO"].includes(status)) return "bg-green-100 text-green-800";
  if (["JA_PAGO", "PENDENTE"].includes(status)) return "bg-blue-100 text-blue-800";
  if (["CANCELADO", "DIVERGENCIA"].includes(status)) return "bg-red-100 text-red-800";
  return "bg-amber-100 text-amber-800";
}

function lerPlanilhas(files: File[]): Promise<{ arquivos: string[]; linhas: LinhaImportada[] }> {
  return Promise.all(files.map(async (file) => {
    const dados = await file.arrayBuffer();
    const workbook = XLSX.read(dados, { type: "array", cellDates: true });
    const linhas: LinhaImportada[] = [];
    for (const abaNome of workbook.SheetNames) {
      const aba = workbook.Sheets[abaNome];
      const matriz = XLSX.utils.sheet_to_json<unknown[]>(aba, { header: 1, raw: true, defval: null });
      const dataMovimentacao = matriz
        .find((linha) => normalizarCabecalho(linha[0]) === "data movimentacao")
        ?.at(1);
      const indiceCabecalho = matriz.findIndex((linha) => {
        const colunas = linha.map(normalizarCabecalho);
        return colunas.includes("seu numero")
          && colunas.includes("vencimento")
          && colunas.includes("valor inicial (r$)")
          && colunas.includes("descricao de operacoes");
      });
      if (indiceCabecalho < 0) continue;
      const cabecalho = matriz[indiceCabecalho].map(normalizarCabecalho);
      const indice = (nome: string) => cabecalho.indexOf(nome);
      for (let posicao = indiceCabecalho + 1; posicao < matriz.length; posicao += 1) {
        const linha = matriz[posicao];
        const descricaoOperacao = texto(linha[indice("descricao de operacoes")]);
        if (!normalizarCabecalho(descricaoOperacao).includes("liquidacao")) continue;
        linhas.push({
          arquivoNome: file.name,
          abaNome,
          numeroLinha: posicao + 1,
          dataMovimentacao: dataExcel(dataMovimentacao),
          pagador: texto(linha[indice("pagador")]) || null,
          seuNumero: texto(linha[indice("seu numero")]) || null,
          nossoNumero: texto(linha[indice("nosso numero")]) || null,
          vencimento: dataExcel(linha[indice("vencimento")]),
          valorInicial: numeroExcel(linha[indice("valor inicial (r$)")]),
          valorFinal: numeroExcel(linha[indice("valor final (r$)")]),
          descricaoOperacao,
        });
      }
    }
    return { arquivo: file.name, linhas };
  })).then((resultado) => ({ arquivos: resultado.map((item) => item.arquivo), linhas: resultado.flatMap((item) => item.linhas) }));
}

function baixarArquivo(nome: string, conteudo: string, tipo: string) {
  const blob = new Blob([conteudo], { type: tipo });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nome;
  link.click();
  URL.revokeObjectURL(url);
}

export default function BaixasFrancesinha() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [baixaAbertaId, setBaixaAbertaId] = useState<number | null>(null);
  const [selecionadas, setSelecionadas] = useState<number[]>([]);
  const [buscaHistorico, setBuscaHistorico] = useState("");
  const [statusHistorico, setStatusHistorico] = useState("");
  const [dataInicialHistorico, setDataInicialHistorico] = useState("");
  const [dataFinalHistorico, setDataFinalHistorico] = useState("");
  const [filtroLinhas, setFiltroLinhas] = useState("");
  const [buscaLinhas, setBuscaLinhas] = useState("");
  const [dataInicialMovimentacao, setDataInicialMovimentacao] = useState("");
  const [dataFinalMovimentacao, setDataFinalMovimentacao] = useState("");
  const filtrosHistorico = useMemo(() => ({
    busca: buscaHistorico || undefined,
    status: statusHistorico || undefined,
    dataInicial: dataInicialHistorico || undefined,
    dataFinal: dataFinalHistorico || undefined,
  }), [buscaHistorico, statusHistorico, dataInicialHistorico, dataFinalHistorico]);
  const historicoQuery = trpc.baixasFrancesinha.listar.useQuery(filtrosHistorico);
  const detalheQuery = trpc.baixasFrancesinha.obter.useQuery({ id: baixaAbertaId ?? 0 }, { enabled: baixaAbertaId !== null });
  const baixa = detalheQuery.data as Baixa | undefined;

  const criarMutation = trpc.baixasFrancesinha.criar.useMutation({
    onSuccess: (resultado) => {
      const criada = resultado as Baixa;
      setBaixaAbertaId(criada.id);
      setSelecionadas([]);
      historicoQuery.refetch();
      toast.success(`Baixa Francesinha #${criada.id} criada.`);
    },
    onError: (erro) => toast.error(erro.message),
  });
  const confirmarMutation = trpc.baixasFrancesinha.confirmar.useMutation({
    onSuccess: (resultado) => {
      toast.success(`${resultado.baixadas} baixa(s) executada(s).`);
      if (resultado.divergentes.length) toast.warning(`${resultado.divergentes.length} linha(s) não foram baixadas por divergência.`);
      setSelecionadas([]);
      detalheQuery.refetch();
      historicoQuery.refetch();
    },
    onError: (erro) => toast.error(erro.message),
  });
  const encerrarMutation = trpc.baixasFrancesinha.encerrar.useMutation({
    onSuccess: (resultado) => {
      toast.success(`Baixa Francesinha ${statusLabel(resultado.status)}.`);
      detalheQuery.refetch();
      historicoQuery.refetch();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const linhasVisiveis = useMemo(() => (baixa?.linhas ?? []).filter((linha) => {
    const atendeStatus = !filtroLinhas || linha.status === filtroLinhas;
    const atendeDataInicial = !dataInicialMovimentacao || (!!linha.dataMovimentacao && linha.dataMovimentacao >= dataInicialMovimentacao);
    const atendeDataFinal = !dataFinalMovimentacao || (!!linha.dataMovimentacao && linha.dataMovimentacao <= dataFinalMovimentacao);
    const termo = buscaLinhas.trim().toLocaleLowerCase("pt-BR");
    const atendeBusca = !termo || [linha.arquivoNome, linha.abaNome, linha.pagador, linha.seuNumero, linha.nossoNumero, linha.contaReceberId, linha.boletoId]
      .some((valor) => texto(valor).toLocaleLowerCase("pt-BR").includes(termo));
    return atendeStatus && atendeDataInicial && atendeDataFinal && atendeBusca;
  }), [baixa?.linhas, filtroLinhas, buscaLinhas, dataInicialMovimentacao, dataFinalMovimentacao]);
  const prontasVisiveis = linhasVisiveis.filter((linha) => linha.status === "PRONTO_PARA_BAIXA");
  const totalSelecionado = (baixa?.linhas ?? []).filter((linha) => selecionadas.includes(linha.id)).reduce((total, linha) => total + Number(linha.valorInicial ?? 0), 0);

  async function importar(files: File[]) {
    try {
      const resultado = await lerPlanilhas(files);
      if (!resultado.linhas.length) {
        toast.error("Nenhuma linha com “liquidação” foi encontrada nos arquivos.");
        return;
      }
      criarMutation.mutate({ arquivos: resultado.arquivos, linhas: resultado.linhas });
    } catch (erro: any) {
      toast.error(erro.message || "Não foi possível ler as planilhas.");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function alternarLinha(id: number, marcado: boolean) {
    setSelecionadas((atual) => marcado ? [...new Set([...atual, id])] : atual.filter((item) => item !== id));
  }

  function alternarTodas(marcado: boolean) {
    const ids = prontasVisiveis.map((linha) => linha.id);
    setSelecionadas((atual) => marcado ? [...new Set([...atual, ...ids])] : atual.filter((id) => !ids.includes(id)));
  }

  function confirmarBaixas() {
    if (!baixa || !selecionadas.length) return;
    confirmarMutation.mutate({ id: baixa.id, linhaIds: selecionadas });
  }

  function encerrar(cancelar: boolean) {
    if (!baixa) return;
    const mensagem = cancelar
      ? "Cancelar esta Baixa Francesinha? Ela não permitirá novas baixas."
      : "Finalizar esta Baixa Francesinha? Ela não permitirá novas baixas.";
    if (!window.confirm(mensagem)) return;
    encerrarMutation.mutate({ id: baixa.id, cancelar });
  }

  function exportarExcel() {
    if (!baixa) return;
    const dados = baixa.linhas.map((linha) => ({
      "Status": statusLabel(linha.status),
      "Arquivo": linha.arquivoNome,
      "Aba": linha.abaNome,
      "Linha": linha.numeroLinha,
      "Data movimentação": dataBr(linha.dataMovimentacao),
      "Pagador": linha.pagador,
      "Seu número": linha.seuNumero,
      "Nosso número": linha.nossoNumero,
      "Vencimento": dataBr(linha.vencimento),
      "Valor inicial": Number(linha.valorInicial ?? 0),
      "Valor final": Number(linha.valorFinal ?? 0),
      "Método de cruzamento": linha.metodoCruzamento,
      "Parcela": linha.contaReceberId,
      "Boleto": linha.boletoId,
      "Mensagem": linha.mensagem,
      "Baixado por": linha.baixadoPorNome,
      "Baixado em": dataHoraBr(linha.baixadoEm),
    }));
    const workbook = XLSX.utils.book_new();
    const planilha = XLSX.utils.json_to_sheet(dados);
    XLSX.utils.book_append_sheet(workbook, planilha, "Resultado");
    XLSX.writeFile(workbook, `baixa-francesinha-${baixa.id}.xlsx`);
  }

  function exportarPdf() {
    if (!baixa) return;
    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const titulo = `Baixa Francesinha #${baixa.id} — ${statusLabel(baixa.status)}`;
    const cabecalho = () => {
      pdf.setFontSize(14);
      pdf.text(titulo, 10, 12);
      pdf.setFontSize(8);
      pdf.text(`Criada em ${dataHoraBr(baixa.criadoEm)} por ${baixa.criadoPorNome || "-"}`, 10, 18);
      pdf.text("Status | Pagador | Seu número | Vencimento | Valor | Parcela | Boleto | Arquivo/Aba/Linha", 10, 25);
    };
    cabecalho();
    let y = 31;
    pdf.setFontSize(7);
    for (const linha of baixa.linhas) {
      if (y > 190) {
        pdf.addPage();
        cabecalho();
        y = 31;
      }
      const textoLinha = `${statusLabel(linha.status)} | ${linha.pagador || "-"} | ${linha.seuNumero || "-"} | ${dataBr(linha.vencimento)} | ${moeda(linha.valorInicial)} | ${linha.contaReceberId || "-"} | ${linha.boletoId || "-"} | ${linha.arquivoNome}/${linha.abaNome}/${linha.numeroLinha}`;
      const partes = pdf.splitTextToSize(textoLinha, 275);
      pdf.text(partes, 10, y);
      y += Math.max(5, partes.length * 3.5);
    }
    pdf.save(`baixa-francesinha-${baixa.id}.pdf`);
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Landmark className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Baixas Francesinha</h1>
            <p className="text-sm text-muted-foreground">Importe liquidações, confira o cruzamento e baixe somente parcelas únicas.</p>
          </div>
        </div>
        <Button onClick={() => inputRef.current?.click()} disabled={criarMutation.isPending}>
          <FileUp className="h-4 w-4 mr-2" />
          {criarMutation.isPending ? "Importando..." : "Nova importação"}
        </Button>
        <input ref={inputRef} type="file" accept=".xlsx" multiple className="hidden" onChange={(event) => {
          const arquivos = Array.from(event.target.files ?? []);
          if (arquivos.length) importar(arquivos);
        }} />
      </div>

      {!baixa ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Envie uma ou mais planilhas XLSX ou abra uma baixa do histórico.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="py-4 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm">
              <span><strong>Baixa:</strong> #{baixa.id}</span>
              <Badge className={statusClass(baixa.status)}>{statusLabel(baixa.status)}</Badge>
              <span><strong>Criada:</strong> {dataHoraBr(baixa.criadoEm)} — {baixa.criadoPorNome || "-"}</span>
              <span><strong>Arquivos:</strong> {baixa.arquivos.join(", ") || "-"}</span>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={exportarExcel}><FileSpreadsheet className="h-4 w-4 mr-2" />Excel</Button>
                <Button size="sm" variant="outline" onClick={exportarPdf}><FileDown className="h-4 w-4 mr-2" />PDF</Button>
                {baixa.status === "PENDENTE" && <Button size="sm" variant="outline" onClick={() => encerrar(false)} disabled={encerrarMutation.isPending}><CheckCircle2 className="h-4 w-4 mr-2" />Finalizar</Button>}
                {baixa.status === "PENDENTE" && <Button size="sm" variant="destructive" onClick={() => encerrar(true)} disabled={encerrarMutation.isPending}><XCircle className="h-4 w-4 mr-2" />Cancelar</Button>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Linhas importadas</CardTitle>
              <div className="flex flex-wrap gap-2 pt-2">
                <Input className="w-[250px]" value={buscaLinhas} onChange={(event) => setBuscaLinhas(event.target.value)} placeholder="Buscar pagador, número, parcela, boleto..." />
                <Input className="w-[155px]" type="date" value={dataInicialMovimentacao} onChange={(event) => setDataInicialMovimentacao(event.target.value)} aria-label="Data inicial de movimentação" />
                <Input className="w-[155px]" type="date" value={dataFinalMovimentacao} onChange={(event) => setDataFinalMovimentacao(event.target.value)} aria-label="Data final de movimentação" />
                <select className="h-10 rounded-md border bg-background px-3 text-sm" value={filtroLinhas} onChange={(event) => setFiltroLinhas(event.target.value)}>
                  <option value="">Todos os resultados</option>
                  <option value="PRONTO_PARA_BAIXA">Pronto para baixa</option>
                  <option value="JA_PAGO">Já pago</option>
                  <option value="AMBIGUO">Ambíguo</option>
                  <option value="NAO_ENCONTRADO">Não encontrado</option>
                  <option value="NAO_ELEGIVEL">Não elegível</option>
                  <option value="BAIXADO">Baixado</option>
                  <option value="DIVERGENCIA">Divergência</option>
                </select>
                <Button variant="outline" size="sm" onClick={() => { setBuscaLinhas(""); setFiltroLinhas(""); setDataInicialMovimentacao(""); setDataFinalMovimentacao(""); }}><RotateCcw className="h-4 w-4 mr-2" />Limpar filtros</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-muted-foreground">
                    <th className="p-2"><Checkbox checked={prontasVisiveis.length > 0 && prontasVisiveis.every((linha) => selecionadas.includes(linha.id))} onCheckedChange={(marcado) => alternarTodas(marcado === true)} disabled={baixa.status !== "PENDENTE" || !prontasVisiveis.length} /></th>
                    <th className="p-2">Resultado</th><th className="p-2">Pagador / números</th><th className="p-2">Vencimento / valor</th><th className="p-2">Parcela / boleto</th><th className="p-2">Origem</th>
                  </tr></thead>
                  <tbody>{linhasVisiveis.map((linha) => <tr key={linha.id} className="border-b align-top">
                    <td className="p-2"><Checkbox checked={selecionadas.includes(linha.id)} onCheckedChange={(marcado) => alternarLinha(linha.id, marcado === true)} disabled={baixa.status !== "PENDENTE" || linha.status !== "PRONTO_PARA_BAIXA"} /></td>
                    <td className="p-2"><Badge className={statusClass(linha.status)}>{statusLabel(linha.status)}</Badge><p className="mt-1 max-w-[240px] text-xs text-muted-foreground">{linha.mensagem}</p>{linha.candidatos.length > 1 && <p className="mt-1 text-xs text-muted-foreground">Candidatas: {linha.candidatos.map((item) => `#${item.contaReceberId}`).join(", ")}</p>}</td>
                    <td className="p-2"><div>{linha.pagador || "-"}</div><div className="text-xs text-muted-foreground">Seu: {linha.seuNumero || "-"} | Nosso: {linha.nossoNumero || "-"}</div></td>
                    <td className="p-2"><div>{dataBr(linha.vencimento)}</div><div>{moeda(linha.valorInicial)}</div></td>
                    <td className="p-2"><div>Parcela: {linha.contaReceberId || "-"}</div><div>Boleto: {linha.boletoId || "-"}</div>{linha.baixadoPorNome && <div className="text-xs text-muted-foreground">{linha.baixadoPorNome} — {dataHoraBr(linha.baixadoEm)}</div>}</td>
                    <td className="p-2 text-xs"><div>{linha.arquivoNome}</div><div>{linha.abaNome} — linha {linha.numeroLinha}</div><div>Mov.: {dataBr(linha.dataMovimentacao)}</div></td>
                  </tr>)}</tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">{selecionadas.length} selecionada(s) — {moeda(totalSelecionado)}</span>
                <Button onClick={confirmarBaixas} disabled={baixa.status !== "PENDENTE" || !selecionadas.length || confirmarMutation.isPending}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />{confirmarMutation.isPending ? "Baixando..." : `Baixar ${selecionadas.length} parcela(s)`}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Histórico da baixa</CardTitle></CardHeader>
            <CardContent><div className="space-y-2">{baixa.historico.map((evento) => <div key={evento.id} className="rounded border p-3 text-sm"><div className="font-medium">{evento.descricao}</div><div className="mt-1 text-xs text-muted-foreground">{dataHoraBr(evento.criadoEm)} — {evento.userName || "Sistema"}</div></div>)}</div></CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" />Histórico de importações</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-wrap gap-2"><Input className="w-[280px]" value={buscaHistorico} onChange={(event) => setBuscaHistorico(event.target.value)} placeholder="Buscar baixa, arquivo, pagador, número, usuário..." /><Input className="w-[155px]" type="date" value={dataInicialHistorico} onChange={(event) => setDataInicialHistorico(event.target.value)} aria-label="Data inicial da importação" /><Input className="w-[155px]" type="date" value={dataFinalHistorico} onChange={(event) => setDataFinalHistorico(event.target.value)} aria-label="Data final da importação" /><select className="h-10 rounded-md border bg-background px-3 text-sm" value={statusHistorico} onChange={(event) => setStatusHistorico(event.target.value)}><option value="">Todos os status</option><option value="PENDENTE">Pendente</option><option value="FINALIZADO">Finalizado</option><option value="FINALIZADO_PARCIALMENTE">Finalizado parcialmente</option><option value="CANCELADO">Cancelado</option></select><Button variant="outline" size="sm" onClick={() => { setBuscaHistorico(""); setStatusHistorico(""); setDataInicialHistorico(""); setDataFinalHistorico(""); }}><Search className="h-4 w-4 mr-2" />Limpar busca</Button></div>
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b text-left text-muted-foreground"><th className="p-2">Baixa</th><th className="p-2">Status</th><th className="p-2">Criada por / em</th><th className="p-2 text-right">Linhas</th><th className="p-2 text-right">Baixadas</th><th className="p-2">Ação</th></tr></thead><tbody>{(historicoQuery.data ?? []).map((item: any) => <tr key={item.id} className="border-b"><td className="p-2">#{item.id}</td><td className="p-2"><Badge className={statusClass(item.status)}>{statusLabel(item.status)}</Badge></td><td className="p-2">{item.criadoPorNome || "-"}<div className="text-xs text-muted-foreground">{dataHoraBr(item.criadoEm)}</div></td><td className="p-2 text-right">{item.totalLinhas ?? 0}</td><td className="p-2 text-right">{item.baixadas ?? 0}</td><td className="p-2"><Button size="sm" variant="outline" onClick={() => { setBaixaAbertaId(item.id); setSelecionadas([]); }}>Abrir</Button></td></tr>)}</tbody></table></div>
        </CardContent>
      </Card>
    </div>
  );
}
